import { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { sendContact } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useStore } from "@/context/StoreContext";
import { Mail, Instagram, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const { settings } = useStore();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email.includes("@") || !form.message) { toast.error("Please fill all fields"); return; }
    setLoading(true);
    try { await sendContact(form); setSent(true); toast.success("Message sent"); }
    catch { toast.error("Could not send. Try again."); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page">
      <section className="mx-auto max-w-7xl px-5 pt-16 lg:px-8 lg:pt-24">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Contact</p>
            <h1 className="mt-4 font-display text-5xl font-extrabold tracking-tighter sm:text-6xl">Let's talk.</h1>
            <p className="mt-6 max-w-md text-muted-foreground">Questions about a guide, a purchase or a refund? Send us a message and we'll get back within 24 hours.</p>
            <div className="mt-10 space-y-4 text-sm">
              <a href={`mailto:${settings?.social_email || "hello@upgrademate.com"}`} className="flex items-center gap-3 hover:underline" data-testid="contact-email-link"><Mail className="h-4 w-4" /> {settings?.social_email || "hello@upgrademate.com"}</a>
              <a href={settings?.social_instagram || "#"} target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:underline" data-testid="contact-instagram-link"><Instagram className="h-4 w-4" /> Instagram</a>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            {sent ? (
              <div className="flex h-full flex-col items-start justify-center border border-black/10 p-10" data-testid="contact-success">
                <CheckCircle2 className="h-8 w-8" />
                <h2 className="mt-4 font-display text-2xl tracking-tight">Message received.</h2>
                <p className="mt-2 text-muted-foreground">Thanks for reaching out. We'll reply to {form.email} shortly.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4 border border-black/10 p-8" data-testid="contact-form">
                <Input data-testid="contact-name" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none h-11 border-black/20 focus-visible:ring-black" />
                <Input data-testid="contact-email" type="email" placeholder="Your email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none h-11 border-black/20 focus-visible:ring-black" />
                <Textarea data-testid="contact-message" placeholder="Your message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-none border-black/20 focus-visible:ring-black" />
                <Button data-testid="contact-submit" disabled={loading} className="w-full rounded-none h-12 bg-primary text-primary-foreground hover:bg-primary/90">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Message"}</Button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
      <div className="py-24" />
    </div>
  );
}
